import { Box, chakra, Text } from '@chakra-ui/react';
import { kycDocumentByCategories } from '@shinetools/kyc-validation';
import { flatten } from 'ramda';

import {
  KycReviewDocumentStatus,
  KycReviewDocumentType,
} from '__generated__/GQL';
import asBentoPage from 'common/bento/hoc/asBentoPage';
import Button from 'components/_core/Button';
import { isKycDocumentReviewable } from 'features/Bento/libs/kycDocuments';
import OBNavigation from 'features/Onboarding/components/OBNavigation/OBNavigation';

import Header from '../../../../components/Header';
import UploadContent from '../../components/UploadContent';
import { AddressModuleData, DocumentFile } from '../../machine/model';
import locales from './locales';

const DOWNLOAD_FORM_URL =
  'https://storage.googleapis.com/shine-public/product/kyc/proof-of-accommodation.pdf';

const SHINE_START_DOWNLOAD_FORM_URL =
  'https://storage.googleapis.com/shine-public/product/kyc/proof-of-accommodation-shine-start.pdf';

const PROOF_OF_ACCOMMODATION_DOCUMENT_TYPES = flatten(
  kycDocumentByCategories.PROOF_OF_ACCOMMODATION,
) as KycReviewDocumentType[];

interface ProofOfAccommodationProps {
  moduleData: AddressModuleData;
  isLoading: boolean;
  isDeleting: boolean;
  isShineStart: boolean;
  isSubmitting: boolean;
  onContinue: () => void;
  onSuccess: (doc: DocumentFile) => void;
  onDeleteClick: (documentId: string) => void;
}

/**
 * A page that asks the user for a proof of accommodation
 */
const ProofOfAccommodation = asBentoPage<ProofOfAccommodationProps>(
  ({
    isDeleting,
    isLoading,
    isShineStart,
    isSubmitting,
    Layout,
    moduleData,
    onContinue,
    onDeleteClick,
    onPrev,
    onSuccess,
  }) => {
    const kycDocuments = moduleData.viewer.companyUser.kycDocuments.filter(
      ({ status }) => status !== KycReviewDocumentStatus.Refused,
    );

    const proofOfAccommodationDocument = kycDocuments.find(
      (doc) => doc && PROOF_OF_ACCOMMODATION_DOCUMENT_TYPES.includes(doc.type),
    );
    const isProofOfAccommodationReviewable = Boolean(
      proofOfAccommodationDocument &&
        isKycDocumentReviewable(proofOfAccommodationDocument),
    );

    return (
      <Layout nav={<OBNavigation onPrev={onPrev} />}>
        <Box>
          <Header description={locales.description} title={locales.title} />

          <Text
            fontWeight="weight-500"
            marginBottom="space-16"
            size="lg"
            variant="primary"
          >
            1. {locales.downloadFormSubtitle}
          </Text>

          <Button
            as={chakra.a}
            href={
              isShineStart ? SHINE_START_DOWNLOAD_FORM_URL : DOWNLOAD_FORM_URL
            }
            icon="download"
            target="_blank"
            variant="inline-primary"
          >
            {locales.downloadFormLink}
          </Button>

          <Text
            fontWeight="weight-500"
            marginBottom="space-16"
            marginTop="space-40"
            size="lg"
            variant="primary"
          >
            {`2. ${
              isProofOfAccommodationReviewable
                ? locales.checkFormSubtitle
                : locales.uploadFormSubtitle
            }`}
          </Text>

          <UploadContent
            areFileActionsDisabled={isDeleting}
            documentTypeToUpload={
              proofOfAccommodationDocument
                ? undefined
                : PROOF_OF_ACCOMMODATION_DOCUMENT_TYPES[0]
            }
            onDeleteFileClick={onDeleteClick}
            onSuccessfulUpload={onSuccess}
            showUploaderLoading={isSubmitting}
            uploadedDocuments={
              proofOfAccommodationDocument ? [proofOfAccommodationDocument] : []
            }
          />

          {isProofOfAccommodationReviewable ? (
            <Button
              isDisabled={isLoading}
              isExpanded
              marginTop="space-48"
              onClick={onContinue}
            >
              {locales.next}
            </Button>
          ) : null}
        </Box>
      </Layout>
    );
  },
);

export default ProofOfAccommodation;
